import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  courseGrade,
  formatShortDate,
  gradeChipLabel,
  hasProvenance,
  formatTimeRange,
  relativeDay,
  type Assignment,
  type ISODate,
  type Lecture,
} from '@app/shared'
import clsx from 'clsx'

import {
  useAnnouncements,
  useAssignments,
  useClock,
  useCourse,
  useFlashcards,
  useLectures,
  useLectureSummary,
  useSyllabus,
} from '../../lib/data'
import { agoPhrase, assignmentStatusLabel, durationMinutes } from '../../components/school/derive'
import { clockFromTimestamp, localDayOf, timeRemaining } from '../../lib/time'
import { MCheck, MInfo } from '../shell/icons'
import { BackRow, Band, Card, Chip, ChipRow, Dot, Eyebrow, SCROLL_BOTTOM, Skeleton } from '../shell/parts'

// ============================================================================
// 1f · COURSE PAGE — docs/design/handoff-mobile/README.md §1f.
//
// "One course, on a phone."
//
// ── THE TWO INVARIANTS THIS SCREEN EXISTS TO CARRY ─────────────────────────
//
// 1. A HIDDEN GRADE IS A DISTINCT MODEL STATE FROM A MISSING ONE, and it renders
//    as the dashed notice, never as an empty slot. `courseGrade()` in
//    @app/shared is a three-way discriminated union precisely so a view cannot
//    collapse 'hidden' and 'none' into one blank cell; this screen renders a
//    different sentence for each.
//
// 2. THE AI SUMMARY BLOCK CANNOT RENDER WITHOUT {model, sourceRef, generatedAt}.
//    `hasProvenance()` is called on the render path, not only in a test, because
//    the type system stops at the network boundary — a cached payload from an
//    older schema arrives shaped like an artifact and carrying nothing. No
//    provenance, no block; the card says a summary has not been made instead.
//
// ── AND THE STUDY BUTTON IS ABSENT, exactly as the README instructs: "Full study
// mode … is desktop-only; 'Study' opens a phone-sized flashcard runner in a later
// pass, and until it exists the button should be absent rather than dead." The
// flashcard COUNT stays, because it is a true fact about what has been made.
// ============================================================================

type Tab = 'assignments' | 'lectures' | 'syllabus' | 'news'

export function MobileCourse() {
  const { courseCode } = useParams()
  const { data: clock } = useClock()
  const { data: course, isPending, error } = useCourse(courseCode)
  const [tab, setTab] = useState<Tab>('assignments')

  const today = clock?.date
  const now = clock?.minutesIntoDay ?? 0

  if (isPending) {
    return (
      <div className="flex flex-1 flex-col gap-3 px-[14px] pt-[14px]">
        <Skeleton height={80} className="rounded-context" />
        <Skeleton height={180} className="rounded-context" />
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="flex flex-1 flex-col gap-3 px-[14px] pt-[14px]">
        <BackRow label="School" />
        <p className="text-[12.5px] leading-[1.55] text-microcopy text-pretty">
          {error
            ? `That course could not be read. ${error instanceof Error ? error.message : ''}`
            : 'There is no course with that code. The School screen lists every course there is.'}
        </p>
      </div>
    )
  }

  const grade = courseGrade(course)
  const meeting = course.meetings[0]

  return (
    <>
      <Band band="course" className="px-[14px] pb-3 pt-[6px]">
        <BackRow label="School" />
        <div className="flex flex-wrap items-baseline gap-[10px]">
          <h1 className="text-[22px] font-semibold leading-none tracking-[-0.02em] text-ink">
            {course.code}
          </h1>
          <span className="text-[13.5px] font-normal leading-[1.3] text-ink-2">{course.name}</span>
        </div>
        <div className="mt-[7px] flex flex-wrap items-center gap-2 text-[11.5px] font-normal leading-none text-muted">
          {/* Canvas lists no teacher on plenty of courses, and it has no meeting
              pattern at all for a Berkeley course — the times live in SIS. Each
              segment renders only when there is one; nothing is filled in. */}
          {course.instructor && <span>{course.instructor}</span>}
          {course.instructor && meeting && <Dot />}
          {meeting && (
            <span>
              {formatTimeRange(meeting.startMinutes, meeting.endMinutes)}
            </span>
          )}
          {meeting?.location && <Dot />}
          {meeting?.location && <span>{meeting.location}</span>}
          {grade.kind === 'score' && (
            <>
              <Dot />
              <span>{gradeChipLabel(grade)}</span>
            </>
          )}
        </div>
      </Band>

      <ChipRow>
        <Chip active={tab === 'assignments'} onClick={() => setTab('assignments')}>
          Assignments
        </Chip>
        <Chip active={tab === 'lectures'} onClick={() => setTab('lectures')}>
          Lectures
        </Chip>
        <Chip active={tab === 'syllabus'} onClick={() => setTab('syllabus')}>
          Syllabus
        </Chip>
        <Chip active={tab === 'news'} onClick={() => setTab('news')}>
          News
        </Chip>
      </ChipRow>

      <div
        className="flex min-h-0 flex-1 flex-col gap-[12px] overflow-y-auto px-[14px]"
        style={{ paddingBottom: SCROLL_BOTTOM }}
      >
        {/* THE HIDDEN-GRADE NOTICE. It sits above everything, on every tab,
            because it is a fact about the whole course and not about a list. */}
        {grade.kind === 'hidden' && (
          <div className="flex flex-none items-start gap-[11px] rounded-card border border-dashed border-dash bg-surface-2 px-[15px] py-[13px]">
            <MInfo className="mt-[1px] flex-none text-muted" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold leading-[1.3] text-ink-2">
                Grades hidden by instructor
              </div>
              <div className="mt-1 text-[12px] font-normal leading-[1.55] text-muted text-pretty">
                Canvas returns no score for this course, so LifeOS shows none. Submissions and due
                dates are current.
              </div>
            </div>
          </div>
        )}

        {tab === 'assignments' && (
          <>
            <AssignmentList courseId={course.id} today={today} nowMinutes={now} />
            <LatestLectureCard courseId={course.id} today={today} nowMinutes={now} />
          </>
        )}
        {tab === 'lectures' && <LectureList courseId={course.id} />}
        {tab === 'syllabus' && <SyllabusPanel courseId={course.id} />}
        {tab === 'news' && <NewsPanel courseId={course.id} today={today} nowMinutes={now} />}
      </div>
    </>
  )
}

function AssignmentList({
  courseId,
  today,
  nowMinutes,
}: {
  courseId: string
  today: ISODate | undefined
  nowMinutes: number
}) {
  const { data, isPending, error } = useAssignments({ courseId })

  if (isPending) return <Skeleton height={160} className="rounded-context" />
  if (error) {
    return (
      <p className="text-[12px] leading-[1.5] text-microcopy text-pretty">
        This course’s assignments could not be read. {error instanceof Error ? error.message : ''}
      </p>
    )
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-[11.5px] leading-[1.45] text-microcopy">
        Canvas has returned no assignments for this course.
      </p>
    )
  }

  return (
    <div className="flex flex-none flex-col rounded-context border border-line bg-surface px-1 py-[6px]">
      {data.map((a, i) => (
        <div key={a.id}>
          {i > 0 && <div className="mx-3 h-px bg-fill" aria-hidden />}
          <AssignmentRow assignment={a} today={today} nowMinutes={nowMinutes} />
        </div>
      ))}
    </div>
  )
}

function AssignmentRow({
  assignment,
  today,
  nowMinutes,
}: {
  assignment: Assignment
  today: ISODate | undefined
  nowMinutes: number
}) {
  const submitted = assignment.status === 'submitted' || assignment.status === 'graded'
  const t = assignment.dueAt && today ? timeRemaining(assignment.dueAt, today, nowMinutes) : null
  const late = t?.tone === 'overdue' && !submitted

  return (
    <div className="flex items-center gap-[11px] p-3">
      {submitted ? (
        <MCheck className="flex-none text-success-dot" />
      ) : (
        <span
          className={clsx('size-[9px] flex-none rounded-full', late ? 'bg-danger-dot' : 'bg-dash')}
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            'text-[14px] leading-[1.25] text-pretty',
            submitted ? 'font-medium text-ink-3' : 'font-semibold text-ink',
          )}
        >
          {assignment.title}
        </div>
        <div
          className={clsx(
            'mt-[5px] text-[11px] leading-none',
            late ? 'font-medium text-danger-2' : submitted ? 'text-microcopy' : 'text-muted',
          )}
        >
          {submitted ? (
            <>
              Submitted
              {/* A grade the instructor hid is not a grade we have. The row says
                  which of the two it is rather than leaving a blank. */}
              {assignment.score === null ? ' · score hidden' : ` · ${assignment.score}/${assignment.pointsPossible}`}
            </>
          ) : assignment.dueAt === null ? (
            'no due date in Canvas'
          ) : late ? (
            `${assignmentStatusLabel(assignment)} · ${t!.label}`
          ) : today ? (
            // `relativeDay` already falls back to the short date beyond a week,
            // so printing both would render "Sep 24 · Sep 24".
            `${relativeDay(localDayOf(assignment.dueAt), today)} · ${clockFromTimestamp(assignment.dueAt)}`
          ) : (
            formatShortDate(localDayOf(assignment.dueAt))
          )}
        </div>
      </div>
      {t && !submitted && (
        <span
          className={clsx(
            'flex-none whitespace-nowrap text-[12px] leading-none',
            t.tone === 'overdue'
              ? 'font-bold text-danger-2'
              : t.tone === 'urgent'
                ? 'font-bold text-urgent-time'
                : 'font-semibold text-muted',
          )}
        >
          {t.label}
        </span>
      )}
    </div>
  )
}

/**
 * THE LATEST LECTURE, with its AI summary and that summary's provenance.
 *
 * `hasProvenance()` gates the whole violet block. A summary without a model, a
 * source and a timestamp does not render — not as an unlabelled paragraph, not
 * as a placeholder. The card then says a summary has not been generated, which
 * is a fact about what has been asked for rather than a wait.
 */
function LatestLectureCard({
  courseId,
  today,
  nowMinutes,
}: {
  courseId: string
  today: ISODate | undefined
  nowMinutes: number
}) {
  const { data: lectures } = useLectures(courseId)
  const recorded = (lectures ?? []).filter((l) => l.status !== 'not_recorded')
  const latest = recorded[recorded.length - 1] ?? null
  const { data: summary } = useLectureSummary(latest?.id)
  const { data: flashcards } = useFlashcards(latest?.id)

  if (!latest) return null

  const provenanced = hasProvenance(summary)
  const minutes = durationMinutes(latest.durationSec ?? null)

  return (
    <Card className="gap-[11px] px-[15px]">
      <div className="flex items-center gap-2">
        <Eyebrow>Latest lecture</Eyebrow>
        {latest.index !== null && (
          <span className="ml-auto text-[10.5px] font-medium uppercase leading-none text-faint">
            {latest.index} of {lectures?.length ?? recorded.length}
          </span>
        )}
      </div>

      <div>
        {/* An unnumbered, untitled recording is a real state (see Lecture.index
            in @app/shared) and it renders under its own date rather than under
            an invented "Lecture 3". */}
        <div className="text-[15px] font-semibold leading-[1.25] text-ink text-pretty">
          {latest.index !== null && `Lecture ${latest.index}`}
          {latest.index !== null && latest.title && ' · '}
          {latest.title ?? (latest.index === null ? `Recording from ${formatShortDate(latest.date)}` : '')}
        </div>
        <div className="mt-[5px] text-[11.5px] font-normal leading-[1.4] text-ink-3">
          {formatShortDate(latest.date)}
          {minutes && ` · ${minutes}`}
          {' · '}
          {latest.status === 'transcribed' ? 'transcript ready' : LECTURE_WAIT[latest.status]}
        </div>
      </div>

      {provenanced && summary ? (
        <div className="flex flex-col gap-[7px] rounded-row border border-accent-line bg-accent-fill px-3 py-[11px]">
          <div className="flex items-center gap-[7px]">
            <span className="size-[6px] rounded-full bg-accent-2" aria-hidden />
            <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.06em] text-accent">
              AI summary
            </span>
          </div>
          {/* The model's own bullets, run together into the paragraph the mockup
              draws. Joined, never rewritten: a phone-shaped summary of a summary
              would be a second model call nobody made. */}
          <p className="text-[12.5px] font-normal leading-[1.55] text-ink-2 text-pretty">
            {summary.bullets.join(' ')}
          </p>
          <p className="text-[10.5px] font-normal leading-[1.4] text-muted">
            {summary.provenance.model} · from {summary.provenance.sourceRef} · generated{' '}
            {today
              ? agoPhrase(summary.provenance.generatedAt, today, nowMinutes)
              : formatShortDate(summary.provenance.generatedAt.slice(0, 10) as ISODate)}
          </p>
        </div>
      ) : (
        <p className="text-[11.5px] font-normal leading-[1.45] text-microcopy text-pretty">
          No summary has been generated for this lecture. Nothing runs on a schedule — a transcript
          landing does not produce study material, so this is a fact about what has been asked for
          rather than a wait.
        </p>
      )}

      {(flashcards?.length ?? 0) > 0 && (
        <div className="flex gap-[9px]">
          <span className="flex h-11 flex-1 items-center justify-center rounded-row bg-fill-2 text-[13px] font-semibold leading-none text-ink-2">
            {flashcards!.length} flashcards
          </span>
          {/* THE STUDY BUTTON IS DELIBERATELY ABSENT. The phone-sized flashcard
              runner is a later pass, and the README says so outright: absent
              rather than dead. */}
        </div>
      )}

      <p className="text-[10.5px] font-normal leading-[1.45] text-microcopy text-pretty">
        Audio stays on the desktop PC. If it is offline, the transcript still opens — playback does
        not.
      </p>
    </Card>
  )
}

const LECTURE_WAIT: Record<Lecture['status'], string> = {
  not_recorded: 'not recorded',
  audio_uploaded: 'queued for the desktop PC',
  processing: 'transcribing on the desktop PC',
  transcribed: 'transcript ready',
  failed: 'transcription failed — the audio is safe',
}

function LectureList({ courseId }: { courseId: string }) {
  const { data, isPending } = useLectures(courseId)
  if (isPending) return <Skeleton height={140} className="rounded-context" />
  if (!data || data.length === 0) {
    return (
      <p className="text-[11.5px] leading-[1.45] text-microcopy">
        No lectures have been recorded for this course.
      </p>
    )
  }
  return (
    <Card className="gap-[11px] px-[15px]">
      {data.map((l) => (
        <div key={l.id} className="flex items-center gap-3">
          <span className="w-[26px] flex-none text-[11px] font-semibold leading-none text-faint">
            {String(l.index).padStart(2, '0')}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold leading-[1.25] text-ink">
              {l.title}
            </span>
            <span className="mt-1 block text-[11px] font-normal leading-none text-muted">
              {formatShortDate(l.date)} · {LECTURE_WAIT[l.status]}
            </span>
          </span>
        </div>
      ))}
    </Card>
  )
}

function SyllabusPanel({ courseId }: { courseId: string }) {
  const { data, isPending } = useSyllabus(courseId)
  if (isPending) return <Skeleton height={120} className="rounded-context" />
  if (!data || !hasProvenance(data)) {
    return (
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        No syllabus has been read for this course. Reading one is a desktop action — the phone
        shows what was extracted, it does not run the extraction.
      </p>
    )
  }
  return (
    <Card className="gap-[10px] px-[15px]">
      <Eyebrow>Syllabus</Eyebrow>
      {data.gradingBreakdown.map((c) => (
        <div key={c.label} className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-[13.5px] font-medium leading-[1.3] text-ink">
            {c.label}
          </span>
          <span className="flex-none text-[12px] font-semibold leading-none text-ink-2">
            {c.weightPct}%
          </span>
        </div>
      ))}
      {/* Every number above came out of a PDF rather than out of Canvas, so the
          whole panel carries one provenance line and cannot render without it. */}
      <p className="text-[10.5px] font-normal leading-[1.4] text-muted text-pretty">
        {data.provenance.model} · extracted from {data.sourceFile}
      </p>
    </Card>
  )
}

function NewsPanel({
  courseId,
  today,
  nowMinutes,
}: {
  courseId: string
  today: ISODate | undefined
  nowMinutes: number
}) {
  const { data, isPending } = useAnnouncements({ courseId })
  if (isPending) return <Skeleton height={120} className="rounded-context" />
  if (!data || data.length === 0) {
    return (
      <p className="text-[11.5px] leading-[1.45] text-microcopy">
        No announcements have come down for this course.
      </p>
    )
  }
  return (
    <Card className="gap-3 px-[15px]">
      {data.map((a) => (
        <div key={a.id} className="flex gap-[10px]">
          <span
            className={clsx(
              'mt-[6px] size-[7px] flex-none rounded-full',
              a.read ? 'bg-dash' : 'bg-danger-dot',
            )}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold leading-[1.25] text-ink text-pretty">
              {a.title}
            </div>
            <div className="mt-[5px] text-[11px] font-normal leading-none text-muted">
              {a.author ?? 'no author in Canvas'}
              {today && ` · ${agoPhrase(a.postedAt, today, nowMinutes)}`}
            </div>
          </div>
        </div>
      ))}
    </Card>
  )
}
